'use client';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import { animated, type SpringValue } from 'react-spring';
import {
    Flödesspår,
    GRUND,
    Gradientfält,
    Pulsnod,
    ScenVäxlare,
    Siffervisare,
    TYPSNITT,
    hexTillRgba,
    ljus,
    type ScenKontext
} from 'src/components/rörelse';
import { fNummerMedSpacing } from 'src/utils/format-number';
import { PresentationsNav } from '../komponenter/PresentationsNav';
import { ScenSektion } from '../komponenter/ScenSektion';
import { hittaPresentation } from '../presentationer';
import { AVIPOSTER, BANOR, HYRESSPÅR, INKOMMANDE, LEGITIMERING, SPRÅKVARIANTER } from './handelser-innehåll';

const AnimeradDiv = animated('div');
const KOMBO = 'lila' as const;

const GLÖDPOSITIONER: [number, number][] = [
    [24, 28],
    [70, 32],
    [40, 58],
    [66, 60],
    [32, 70],
    [58, 40]
];

export function HändelserView() {
    const presentation = hittaPresentation('handelser');
    const [glödIndex, sättGlödIndex] = useState(0);
    const glöd = useCallback(
        (index: number) => (synlig: boolean) => {
            if (synlig) sättGlödIndex(index);
        },
        []
    );

    return (
        <>
            <Gradientfält
                kombinationer={GLÖDPOSITIONER.map(() => KOMBO)}
                positioner={GLÖDPOSITIONER}
                index={glödIndex}
                styrka={0.4}
            />

            <ScenSektion
                id="inkommande"
                kombo={KOMBO}
                nivå="h1"
                rubrik={presentation.titel}
                brödtext={[
                    presentation.ingress,
                    'Post kommer in i föreningens e-post dygnet runt. Var femtonde minut hämtas den, bilagorna sparas, och det som ser ut som en faktura läses av och föreslås som bokföringsunderlag.'
                ]}
                onSynlig={glöd(0)}
            >
                {({ framsteg }: ScenKontext) => <Inkorg framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="banor"
                kombo={KOMBO}
                spegla
                rubrik="Och sorterar sig efter vad de är"
                brödtext={[
                    'En faktura ska bokföras. En ansökan ska beslutas. En felanmälan ska åtgärdas och följas upp. Typen avgör vem som får se den, vad som händer härnäst och när den räknas som avslutad.'
                ]}
                onSynlig={glöd(1)}
            >
                {({ framsteg }: ScenKontext) => <Banor framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="bankid"
                kombo={KOMBO}
                rubrik="Den som agerar är den de säger sig vara"
                brödtext={[
                    'Boende, mäklare och hantverkare rör sig alla genom föreningens ärenden. Legitimeringen sker med BankID, och först därefter kopplas personen till sin roll och sina behörigheter.',
                    'Det är också vad som gör en signatur på ett protokoll eller ett avtal värd något.'
                ]}
                onSynlig={glöd(2)}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                    <Flödesspår
                        noder={LEGITIMERING}
                        kombo={KOMBO}
                        riktning="lodrät"
                        etikett="Vägen från aktör till behörighet"
                    />
                    <Pulsnod kombo={KOMBO} etikett="Väntar på BankID" storlek={16} />
                </Box>
            </ScenSektion>

            <ScenSektion
                id="avi"
                kombo={KOMBO}
                spegla
                rubrik="Hushållets avi bär mer än avgiften"
                brödtext={[
                    'Medlemsavgiften debiteras per hushåll, matchas mot inbetalningarna på kontot och påminns om automatiskt. Men en avi är sällan en enda post.',
                    'El, garageplats, förrådslokal och bokningsavgifter ligger som egna rader med egen moms och egen intäktstyp — hämtade där uppgifterna redan finns, i stället för att skrivas in en gång till.'
                ]}
                onSynlig={glöd(3)}
            >
                <Avi />
            </ScenSektion>

            <ScenSektion
                id="hyra"
                kombo={KOMBO}
                vision
                rubrik="Två sorters hyresgäst, ett kontrakt"
                brödtext={[
                    'En lokalhyresgäst i föreningens egen hyresrätt och en andrahandsboende i någon annans bostadsrätt har lite gemensamt — utom att båda vilar på en handling med parter, tider, uppsägningstid och hyra.',
                    'Hyreskontraktet är den handlingen: sina personer, sin uppsägningstid, sin indexuppräkning och sin koppling till hushållets avi.'
                ]}
                onSynlig={glöd(4)}
            >
                <Box sx={{ width: '100%', maxWidth: 440 }}>
                    <ScenVäxlare
                        etikett="Två slags hyresförhållanden"
                        kombo={KOMBO}
                        höjd={230}
                        scener={HYRESSPÅR.map((h) => ({
                            nyckel: h.nyckel,
                            etikett: h.etikett,
                            innehåll: <Kontraktskort rubrik={h.rubrik} rader={h.rader} />
                        }))}
                    />
                </Box>
            </ScenSektion>

            <ScenSektion
                id="sprak"
                kombo={KOMBO}
                spegla
                vision
                rubrik="På det språk mottagaren faktiskt läser"
                brödtext={[
                    'Varje person i medlemsregistret bär sitt språk. När det skiljer sig från meddelandets finns en översättningsknapp — i trådar, kanaler, utskick och kallelser.',
                    'Originalet står kvar. Översättningen är ett tillägg, inte en ersättning, så ingen behöver undra vad avsändaren faktiskt skrev.'
                ]}
                onSynlig={glöd(5)}
            >
                <Språkväxling />
            </ScenSektion>

            <PresentationsNav nuvarande="handelser" />
        </>
    );
}

function Inkorg({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box
            aria-label="Inkommande handlingar"
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25, maxWidth: 520 }}
        >
            {INKOMMANDE.map((post, i) => {
                const tröskel = i / INKOMMANDE.length;
                const sned = (i % 3) - 1;
                return (
                    <AnimeradDiv
                        key={post.id}
                        style={{
                            opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.25)),
                            transform: framsteg.to(
                                (p) =>
                                    `translate(${p >= tröskel ? 0 : sned * 26}px, ${
                                        p >= tröskel ? 0 : -22
                                    }px) rotate(${p >= tröskel ? 0 : sned * 4}deg)`
                            ),
                            willChange: 'transform, opacity'
                        }}
                    >
                        <Etikett text={post.etikett} />
                    </AnimeradDiv>
                );
            })}
        </Box>
    );
}

function Banor({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 520, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {BANOR.map((bana, i) => {
                const iBanan = INKOMMANDE.filter((d) => d.bana === bana.id);
                const tröskel = i / BANOR.length;
                return (
                    <Box key={bana.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                width: 104,
                                flexShrink: 0,
                                fontSize: 13,
                                color: GRUND.dämpad,
                                textAlign: 'right'
                            }}
                        >
                            {bana.etikett}
                        </Box>
                        <AnimeradDiv
                            aria-hidden
                            style={{
                                width: 22,
                                height: 1,
                                background: ljus(KOMBO),
                                transformOrigin: 'left',
                                transform: framsteg.to((p) => `scaleX(${p >= tröskel ? 1 : 0})`)
                            }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', minWidth: 0 }}>
                            {iBanan.map((d) => (
                                <AnimeradDiv
                                    key={d.id}
                                    style={{
                                        opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.22)),
                                        transform: framsteg.to(
                                            (p) => `translateX(${p >= tröskel ? 0 : 34}px)`
                                        )
                                    }}
                                >
                                    <Etikett text={d.etikett} liten />
                                </AnimeradDiv>
                            ))}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

function Etikett({ text, liten }: { text: string; liten?: boolean }) {
    return (
        <Box
            sx={{
                px: liten ? 1.15 : 1.5,
                py: liten ? 0.5 : 0.75,
                borderRadius: 999,
                fontSize: liten ? 11.5 : 13,
                whiteSpace: 'nowrap',
                color: GRUND.text,
                bgcolor: hexTillRgba(ljus(KOMBO), 0.14),
                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.38)}`
            }}
        >
            {text}
        </Box>
    );
}

function Avi() {
    const summa = AVIPOSTER.reduce((acc, p) => acc + p.belopp, 0);
    return (
        <Box sx={{ width: '100%', maxWidth: 440 }}>
            {AVIPOSTER.map((post) => (
                <Box
                    key={post.id}
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        gap: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${GRUND.kant}`
                    }}
                >
                    <Box sx={{ fontSize: 14.5, color: GRUND.text, minWidth: 0 }}>{post.etikett}</Box>
                    <Box data-tal sx={{ fontSize: 14.5, color: GRUND.text, whiteSpace: 'nowrap' }}>
                        {fNummerMedSpacing(post.belopp)} kr
                    </Box>
                </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, pt: 2.25 }}>
                <Box sx={{ fontSize: 13.5, color: GRUND.dämpad }}>Att betala</Box>
                <Siffervisare
                    till={summa}
                    suffix=" kr"
                    fördröjning={420}
                    variant="h3"
                    component="span"
                    sx={{
                        fontFamily: TYPSNITT.display,
                        fontVariationSettings: "'opsz' 72",
                        fontWeight: 600,
                        fontSize: 'clamp(1.5rem, 3.4vw, 2.2rem)',
                        letterSpacing: '-0.03em',
                        color: ljus(KOMBO),
                        whiteSpace: 'nowrap'
                    }}
                />
            </Box>
        </Box>
    );
}

function Kontraktskort({ rubrik, rader }: { rubrik: string; rader: string[][] }) {
    return (
        <Box
            sx={{
                height: '100%',
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.32)}`,
                bgcolor: hexTillRgba('#12151F', 0.8)
            }}
        >
            <Box
                sx={{
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 36",
                    fontSize: '1.15rem',
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: 2,
                    color: GRUND.text
                }}
            >
                {rubrik}
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', rowGap: 1, columnGap: 2, fontSize: 13.5 }}>
                {rader.map(([etikett, värde]) => (
                    <Box key={etikett} sx={{ display: 'contents' }}>
                        <Box sx={{ color: GRUND.dämpad }}>{etikett}</Box>
                        <Box sx={{ color: GRUND.text, textAlign: 'right' }}>{värde}</Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function Språkväxling() {
    return (
        <Box sx={{ width: '100%', maxWidth: 440 }}>
            <ScenVäxlare
                etikett="Samma meddelande på mottagarens språk"
                kombo={KOMBO}
                höjd={150}
                auto={3600}
                scener={SPRÅKVARIANTER.map((v) => ({
                    nyckel: v.nyckel,
                    etikett: v.etikett,
                    innehåll: (
                        <Box
                            lang={v.nyckel}
                            dir={v.nyckel === 'ar' ? 'rtl' : 'ltr'}
                            sx={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                p: 2.5,
                                borderRadius: 2,
                                border: `1px solid ${hexTillRgba(ljus(KOMBO), 0.32)}`,
                                bgcolor: hexTillRgba('#12151F', 0.8),
                                fontSize: '1.05rem',
                                lineHeight: 1.5,
                                color: GRUND.text
                            }}
                        >
                            {v.text}
                        </Box>
                    )
                }))}
            />
        </Box>
    );
}
